import React from 'react';
import './validate-one-function-schema-n-one-payload.css';
import { useCallback, useState } from 'react';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Stack from 'react-bootstrap/Stack';
import { Alert } from 'react-bootstrap';
import FloatingLabel from 'react-bootstrap/FloatingLabel';
import 'bootstrap/dist/css/bootstrap.min.css';
import * as addFormats from 'ajv-formats';
import { Link } from 'react-router-dom';
import parseJSON from '../../helpers/parse-json';
import prettify from '../../helpers/prettify';
import getAjv from '../../helpers/get-ajv';
import jsonSchemaParametersTemplate from '../../templates/json-schema-parameters.json';
import jsonPayloadParametersTemplate from '../../templates/json-payload-arguments.json';

const ajv = getAjv();
addFormats(ajv)

const textArea = { width: '800px', margin: '20px', fontFamily: 'Courier New' }
const link = { width: '200px', height: '50px', margin: '10px', padding: '5px', color: 'red', backgroundColor: 'lightgray', textAlign: 'center' }

function ValidateOneFunctionSchemaNOnePayload() {
  const [alertMsg, setAlertMsg] = useState('');
  const [alertVariant, setAlertVariant] = useState('info');
  const [errors, setErrors] = useState([]);
  const [functionJsonSchema, setFunctionJsonSchema] = useState('');
  const [payload, setPayload] = useState('');
  const [result, setResult] = useState([]);

  const clearOutputs = useCallback(() => {
    setErrors([]);
    setResult([]);
  }, [setErrors, setResult]);

  const handleValidate = useCallback((mode) => {
    const ajv = getAjv(mode); // options can be passed, e.g. {allErrors: true}
    addFormats(ajv);
    clearOutputs();

    let valid = true;
    let validate;
    try {
      validate = ajv.compile(functionJsonSchema);
    }
    catch (e) {
      valid = false;
      setErrors([e.message]);
      setAlertVariant('danger')
    }

    if (valid && payload) {
      valid = validate(payload);
      if (!valid) {
        const newErrors = validate.errors ?? [];
        setErrors(errors => [...errors, ...newErrors]);
        console.warn(validate.errors);
      }
    }

    setResult([valid]);
  }, [functionJsonSchema, payload, setResult, setErrors, clearOutputs]);

  const checkParsedJSON = useCallback((parsed, type = '') => {
    if (!parsed) {
      setAlertMsg(`${type} JSON is not parsed!`);
      setAlertVariant('danger');
      return false;
    }
    else if (Object.keys(parsed).length === 0) {
      setAlertMsg('JSON is empty!');
      setAlertVariant('info')
    }

    return true;
  }, []);

  const handleFunctionJsonSchemaChange = useCallback((e) => {
    if (!e.target.value) {
      return;
    }

    const parsed = parseJSON(e.target.value);

    if (!checkParsedJSON(parsed, 'Function Json Schema')) {
      setFunctionJsonSchema();

      return;
    }

    clearOutputs();

    prettify('functionJsonSchema')

    setFunctionJsonSchema(parsed);
  }, [clearOutputs]);

  const handlePayloadChange = useCallback((e) => {
    if (!e.target.value) {
      return;
    }

    const parsed = parseJSON(e.target.value);

    if (!checkParsedJSON(parsed, 'Payload')) {
      return;
    }

    clearOutputs();

    prettify('payload');

    setPayload(parsed);
  }, [setPayload, clearOutputs]);

  return (
    <Stack gap={3} direction='vertical' autoCorrect='' className="ValidateOneFunctionSchemaNOnePayload">
      <FloatingLabel as='header' className="ValidateOneFunctionSchemaNOnePayload-header">
        JSON schema validator
        <Stack direction='horizontal' style={{ alignItems: 'center' }}>
          <Link style={link} to='/'>Main page</Link>
        </Stack>
      </FloatingLabel>

      {
        alertMsg &&
        <Alert variant='warning' dismissible>{alertMsg}</Alert>
      }

      <Stack direction='horizontal' gap={3} className="mx-auto"  >
        <Form.Control id="functionJsonSchema" placeholder={'E.g.,\n' + JSON.stringify(jsonSchemaParametersTemplate, undefined, 1)} style={textArea} onBlur={handleFunctionJsonSchemaChange} as="textarea" rows={20} />
        <Form.Control id="payload" placeholder={'E.g.,\n' + JSON.stringify(jsonPayloadParametersTemplate, undefined, 1)} style={textArea} onBlur={handlePayloadChange} as="textarea" rows={20} />
      </Stack>
      <Stack direction='horizontal' className="mx-auto">
        <Button onClick={handleValidate} style={{ width: '100px', margin: '0 10px' }}>Validate</Button>
        <Button onClick={() => handleValidate('2019')} variant='secondary' style={{ width: '100px', margin: '0 10px' }}>ajv-2019</Button>
        <Button onClick={() => handleValidate('2020')} variant='secondary' style={{ width: '100px', margin: '0 10px' }}>ajv-2020</Button>
      </Stack>
      <Stack direction='horizontal' className="mx-auto"  >
        <FloatingLabel as='h4' htmlFor='result'> Result: </FloatingLabel>
        <FloatingLabel id='result' as='div'>
          {
            result?.map((res, idx) =>
              res ?
                <span id="result" key={`res-${idx}`} style={{ color: 'green' }}> &#10003; </span>
                :
                <span id="result" key={`res-${idx}`}>  &#10060; </span>
            )
          }
        </FloatingLabel>
      </Stack >

      <Stack direction='horizontal' className="mx-auto"  >
        <FloatingLabel as='h4' htmlFor='errors'> Errors: </FloatingLabel>
        <FloatingLabel id='errors' as='div'>
          {
            errors?.map((error, idx) => Object.keys(error).length > 0 &&
              <Alert dismissible variant={alertVariant} key={`err-${idx}`}>
                {idx + 1}. {JSON.stringify(error)}
              </Alert>
            )
          }
        </FloatingLabel>
      </Stack>
    </Stack >
  );
}

export default ValidateOneFunctionSchemaNOnePayload;
